<template>
	<div
		v-if="ready"
		class="container"
	>

		<div class="row center">
			<div class="col-lg-8 col-md-12">
				<h1 class="main-content-title">
					{{ translations.navigation.security }}
				</h1>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-8 col-md-12">
				<div class="box">
					<div class="box-head with-line">
						<h3>
							{{ translations.labels.passwordChange }}
						</h3>
						<div class="box-head-text">
							{{ translations.labels.passwordChangeText }}
						</div>
					</div>
					<div class="box-body">
						<el-form
							@submit.prevent.native="submitForm('form')"
							:model="form"
							:rules="rules"
							ref="form"
						>
							<div class="row">
								<div class="col-lg-3 col-sm-12">
									<label class="required with-margin-top">
										{{ translations.labels.currentPassword }}
									</label>
								</div>
								<div class="col-lg-9 col-sm-12">
									<el-form-item prop="password">
										<el-input
											v-model="form.password"
											:placeholder="translations.labels.enterCurrentPassword"
											:minlength="passwordLength"
											type="password"
											autocomplete="off"
											show-password
										/>
									</el-form-item>
								</div>
							</div>
							<div class="row">
								<div class="col-lg-3 col-sm-12">
									<label class="required with-margin-top">
										{{ translations.labels.newPassword }}
									</label>
								</div>
								<div class="col-lg-9 col-sm-12">
									<el-form-item prop="newPassword">
										<el-input
											v-model="form.newPassword"
											:placeholder="translations.labels.enterNewPassword"
											:minlength="passwordLength"
											type="password"
											autocomplete="off"
											show-password
											@focus="showMeter(true)"
											@change="showMeter(false)"
										/>
										<password
											v-if="showPasswordMeter"
											v-model="form.newPassword"
											:secureLength="passwordLength"
											:strength-meter-only="true"
											@score="showScore"
										/>
									</el-form-item>
								</div>
							</div>
							<div class="row">
								<div class="col-lg-3 col-sm-12">
									<label class="required with-margin-top">
										{{ translations.labels.confirmNewPassword }}
									</label>
								</div>
								<div class="col-lg-9 col-sm-12">
									<el-form-item prop="confirmPassword">
										<el-input
											v-model="form.confirmPassword"
											:placeholder="translations.labels.verifyNewPassword"
											type="password"
											autocomplete="off"
											show-password
										/>
									</el-form-item>
								</div>
							</div>
							<div class="row">
								<div class="col-lg-12 align-right">
									<el-form-item class="no-margin-bottom">
										<el-button
											:disabled="isDisabledPassword"
											:loading="processing.update"
											:type="isDisabledPassword ? 'info' : 'primary'"
											native-type="submit"
											class="mobile-wide"
											plain
										>
											{{ translations.labels.updatePassword }}
										</el-button>
									</el-form-item>
								</div>
							</div>
						</el-form>
					</div>
				</div>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-8 col-md-12">
				<div class="box">
					<div class="box-head with-line">
						<h3>
							{{ translations.labels.recentSessions }}
						</h3>
						<div class="box-head-text">
							{{ translations.labels.recentSessionsText }}
						</div>
						<div class="box-head-fusnote">
							{{ translations.labels.recentSessionsNote }} <a href="https://www.maxmind.com" target="_blank">www.maxmind.com</a>
						</div>
					</div>
					<div class="box-body">
						<el-table
							v-if="tableData.length"
							:data="tableData"
							style="width: 100%"
							max-height="400"
						>
							<el-table-column :label="translations.labels.loggedIn" width="160">
								<template slot-scope="scope">
									{{ convertDate(scope.row.createdAt, 'dateTime') }}
								</template>
							</el-table-column>
							<el-table-column :label="translations.labels.ipLocation" width="180">
								<template slot-scope="scope">
									{{ scope.row.ipAddress }}
									<div class="flex-between">
										<div v-if="scope.row.countryCode !== null" class="flag-icon" :style="{ backgroundImage: `url(/images/flags/${convertCountryCode(scope.row.countryCode)}.png)` }" />
										<div class="flag-label">
											{{ scope.row.cityName }}<template v-if="scope.row.countryCode !== null">, {{ scope.row.countryCode }}</template>
										</div>
									</div>
								</template>
							</el-table-column>
							<el-table-column :label="translations.labels.userAgent">
								<template slot-scope="scope">
									<div style="font-size: 85%; line-height: 1.6;">
										{{ scope.row.userAgent }}
									</div>
								</template>
							</el-table-column>
						</el-table>
						<el-empty
							v-else
							:description="translations.labels.noSessions"
							:image-size="150"
						/>
					</div>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
import Password from 'vue-password-strength-meter'

import { _changePassword, _loginSessions } from '@/services/api/account'

import loadTranslations from '@/services/mixins/loadTranslations'
import convertDate from '@/services/mixins/convertDate'

export default {
	name: 'Profile',
	metaInfo () {
		return {
			title: this.translations.navigation.security
		}
	},
	mixins: [
		loadTranslations,
		convertDate
	],
	components: {
		Password
	},
	data () {
		const checkNewPassword = (rule, value, callback) => {
			this.score < 3 ? callback(new Error(this.$store.state.translations.messages.passwordStrength)) : callback()
			if (value !== undefined && value !== '') {
				this.form.password === undefined
					? this.rules.password[0].required = true
					: callback()

				this.form.confirmPassword === undefined
					? this.rules.confirmPassword[0].required = true
					: callback()
			} else {
				this.rules.password[0].required = false
				this.rules.newPassword[0].required = false
				this.rules.confirmPassword[0].required = false
				callback()
			}
		}
		const checkVerifyPassword = (rule, value, callback) => {
			if (this.form.newPassword !== undefined && this.form.newPassword !== '') {
				if (value === undefined || value === '') {
					callback(new Error(this.translations.messages.confirmNewPassword))
				} else {
					value !== this.form.newPassword
						? callback(new Error(this.translations.messages.confirmNewPassword))
						: callback()
				}
			} else {
				callback()
			}
		}
		return {
			form: {
				password: null,
				newPassword: null,
				confirmPassword: null
			},
			passwordLength: 6,
			processing: {
				update: false
			},
			rules: {
				password: [
					{ required: true, message: this.$store.state.translations.messages.enterCurrentPassword, trigger: 'blur' }
				],
				newPassword: [
					{ required: true, message: this.$store.state.translations.messages.enterNewPassword, trigger: 'blur' },
					{ min: this.passwordLength, message: this.$store.state.translations.messages.passwordLength, trigger: 'blur' },
					{ validator: checkNewPassword, trigger: 'blur' }
				],
				confirmPassword: [
					{ required: true, message: this.$store.state.translations.messages.verifyNewPassword, trigger: 'blur' },
					{ min: this.passwordLength, message: this.$store.state.translations.messages.passwordLength, trigger: 'blur' },
					{ validator: checkVerifyPassword, trigger: 'blur' }
				]
			},
			score: 0,
			showPasswordMeter: false,
			tableData: []
		}
	},
	computed: {
		isDisabledPassword () {
			return !this.form.password || !this.form.newPassword || !this.form.confirmPassword || this.score < 2
		}
	},
	created () {
		this.ajaxLoadData()
	},
	methods: {

		ajaxLoadData () {
			_loginSessions()
				.then(response => {
					this.tableData = response
					this.removeLoader()
				})
		},
		ajaxUpdatePassword () {
			this.processing.update = true
			_changePassword(this.form)
				.then(response => {
					this.loadTranslations(response.settings.language)
					this.$store.dispatch('setUser', response)
					setTimeout (() => {
						this.$refs.form.clearValidate()
						this.displayMessage(this.translations.messages.successUpdatePassword, 'success')
						this.processing.update = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.update = false
					}, 200)
				})
		},

		submitForm (form) {
			this.$refs[form].validate((valid) => {
				if (valid) {
					this.ajaxUpdatePassword()
				} else {
					return false
				}
			})
		},
		convertCountryCode (value) {
			if (value) {
				return value.toLowerCase()
			}
		},
		showScore (score) {
			this.score = score
		},
		showMeter (value) {
			if (value === true) {
				this.showPasswordMeter = true
			} else {
				this.showPasswordMeter = this.form.newPassword.length ? true : false
			}
		}

	}
}
</script>
